import { useEffect, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IDevExTeamMappingRule, ITeam } from '@skystone/shared';
import { teamApi } from '@/services/team-api';
import { devExApi } from '@/services/devex-api';
import {
  Badge,
  PageHeader,
  PageLoading,
  ErrorBanner,
} from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { getErrorMessage } from '@/lib/utils';

export function AdminTeamDetailsPage() {
  const { id } = useParams<{ id: string }>();

  const [team, setTeam] = useState<ITeam | null>(null);
  const [mappingRules, setMappingRules] = useState<IDevExTeamMappingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [teamResponse, rulesResponse] = await Promise.all([
        teamApi.getById(id),
        devExApi.listMappingRules(id),
      ]);
      setTeam(teamResponse);
      setMappingRules(rulesResponse);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData().catch(() => undefined);
  }, [loadData]);

  if (loading) {
    return <PageLoading />;
  }

  if (!team) return <div>Team not found</div>;

  return (
    <PageContainer variant="narrow" className="space-y-6">
      <PageHeader
        backTo="/admin/teams"
        title={`Team: ${team.name}`}
        action={
          <div className="flex items-center gap-3">
            <Badge variant="success">Source: Fabric</Badge>
            {team.fabricSyncedAt && <Badge variant="secondary">Last sync: {new Date(team.fabricSyncedAt).toLocaleString()}</Badge>}
            {team.archived ? <Badge variant="warning">Archived</Badge> : <Badge variant="success">Active</Badge>}
          </div>
        }
      />

      <ErrorBanner message={error} />

      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Team Details</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <span className="text-sm font-medium text-muted-foreground">Team Name</span>
            <p className="text-sm">{team.name}</p>
          </div>

          <div className="space-y-1">
            <span className="text-sm font-medium text-muted-foreground">Product</span>
            <p className="text-sm">{team.productName ?? 'Unassigned'}</p>
          </div>

          <div className="space-y-1">
            <span className="text-sm font-medium text-muted-foreground">Region</span>
            <p className="text-sm">{team.regionName ?? 'Not set'}</p>
          </div>

          <div className="space-y-1">
            <span className="text-sm font-medium text-muted-foreground">Fabric Team ID</span>
            <p className="text-sm font-mono">{team.fabricTeamId ?? 'N/A'}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-1">DevEx Survey Mapping Rules</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Survey team name strings that resolve to this team during quarterly data ingestion.
        </p>
        {mappingRules.length === 0 ? (
          <p className="text-sm text-muted-foreground">No mapping rules found for this team.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">Survey Team Name</th>
                <th className="pb-2 pr-4 font-medium">Rule Type</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {mappingRules.map((rule) => (
                <tr key={rule.id} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-mono">{rule.sourceTeamValue}</td>
                  <td className="py-2 pr-4">
                    <Badge variant={rule.ruleType === 'exact' ? 'secondary' : 'default'}>
                      {rule.ruleType === 'exact' ? 'Exact' : 'Alias'}
                    </Badge>
                  </td>
                  <td className="py-2">
                    <Badge variant={rule.status === 'active' ? 'success' : 'warning'}>
                      {rule.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PageContainer>
  );
}
